import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { marked } from "marked";
import { ArrowLeft, FloppyDisk, Eye } from "@phosphor-icons/react";

const blank = { title: "", excerpt: "", body: "", cover_image: "", tag: "field-notes", published: true };

export default function AdminBlogEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [post, setPost] = useState(blank);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isNew) {
      api.get(`/admin/blog/${id}`).then((r) => setPost(r.data));
    }
  }, [id, isNew]);

  const save = async () => {
    setErr(""); setLoading(true);
    try {
      const payload = {
        title: post.title,
        excerpt: post.excerpt,
        body: post.body,
        cover_image: post.cover_image || "",
        tag: post.tag || "field-notes",
        published: !!post.published,
      };
      if (isNew) {
        const { data } = await api.post("/admin/blog", payload);
        nav(`/admin/blog/${data.id}`, { replace: true });
      } else {
        await api.put(`/admin/blog/${id}`, payload);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setPost({ ...post, [k]: e.target.value });

  return (
    <div data-testid="admin-blog-editor" className="fade-up">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
        <div>
          <Link to="/admin/blog" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--secondary)", textDecoration: "none" }} data-testid="back-to-blog">
            <ArrowLeft size={12} weight="bold" /> Back to posts
          </Link>
          <h1 className="page-title" style={{ marginTop: 10 }}>{isNew ? "New post" : "Edit post"}</h1>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-outline" onClick={() => setPreview(!preview)} data-testid="toggle-preview-btn">
            <Eye size={16} weight="bold" /> {preview ? "Edit" : "Preview"}
          </button>
          <button className="btn btn-primary" onClick={save} disabled={loading || !post.title || !post.body} data-testid="save-post-btn">
            <FloppyDisk size={16} weight="bold" /> {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {err && <div className="badge badge-danger" style={{ marginBottom: 14 }}>{err}</div>}

      {preview ? (
        <article className="card" style={{ padding: 40, maxWidth: 820, margin: "0 auto" }}>
          {post.cover_image && (
            <div style={{ marginBottom: 24, borderRadius: 16, overflow: "hidden" }}>
              <img src={post.cover_image} alt="" style={{ width: "100%", maxHeight: 360, objectFit: "cover", display: "block" }} />
            </div>
          )}
          <span className="badge badge-soft">{post.tag}</span>
          <h1 className="font-display" style={{ fontSize: 44, marginTop: 12, lineHeight: 1.05 }}>{post.title || "Untitled"}</h1>
          <p style={{ fontSize: 17, color: "var(--text-soft)", marginTop: 12, lineHeight: 1.55 }}>{post.excerpt}</p>
          <div className="prose" style={{ marginTop: 24 }} dangerouslySetInnerHTML={{ __html: marked.parse(post.body || "*nothing yet*") }} />
        </article>
      ) : (
        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            <div>
              <label className="label">Title</label>
              <input className="input" value={post.title} onChange={set("title")} placeholder="A great headline…" data-testid="post-title-input" style={{ fontSize: 18 }} />
            </div>
            <div>
              <label className="label">Short excerpt (1–2 sentences)</label>
              <textarea className="textarea" rows={2} value={post.excerpt} onChange={set("excerpt")} placeholder="Hook the reader." data-testid="post-excerpt-input" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 200px 140px", gap: 12 }} className="post-meta-grid">
              <div>
                <label className="label">Cover image URL</label>
                <input className="input" value={post.cover_image} onChange={set("cover_image")} placeholder="https://…" data-testid="post-cover-input" />
              </div>
              <div>
                <label className="label">Tag</label>
                <select className="select" value={post.tag} onChange={set("tag")} data-testid="post-tag-input">
                  {["field-notes", "philosophy", "forecasts", "case-study", "tutorial", "news"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select className="select" value={post.published ? "published" : "draft"} onChange={(e) => setPost({ ...post, published: e.target.value === "published" })} data-testid="post-status-input">
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
            <div>
              <label className="label">Body (Markdown supported — # headings, **bold**, lists)</label>
              <textarea className="textarea" rows={20} value={post.body} onChange={set("body")} placeholder="Write your post in Markdown…" data-testid="post-body-input" style={{ fontFamily: "ui-monospace, monospace", fontSize: 14, lineHeight: 1.6 }} />
              <div style={{ fontSize: 12, color: "var(--muted-fg)", marginTop: 6 }}>Tip: <code>## Heading</code> for sections, <code>**bold**</code>, <code>- bullet</code> for lists.</div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .prose h2 { font-family: 'DM Serif Display', serif; font-size: 28px; margin: 24px 0 12px; }
        .prose h3 { font-family: 'DM Serif Display', serif; font-size: 22px; margin: 20px 0 10px; }
        .prose p { font-size: 16.5px; line-height: 1.7; margin: 12px 0; color: var(--text-soft); }
        .prose ul, .prose ol { padding-left: 24px; margin: 12px 0; }
        .prose li { font-size: 16.5px; line-height: 1.7; margin: 6px 0; color: var(--text-soft); }
        .prose strong { color: var(--text); }
        .prose a { color: var(--secondary); text-decoration: underline; }
        @media (max-width: 760px) { .post-meta-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </div>
  );
}
